import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import AgentSelectionModal from './AgentSelectionModal';

const PricingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [showAgentSelection, setShowAgentSelection] = useState(false);

  // useEffect(() => {
  //   // Redirect to home if user is not logged in
  //   if (!user) {
  //     navigate('/');
  //   }
  // }, [user, navigate]);

  const pricingPlans = [
    {
      id: 'Explorer',
      name: 'Explorer',
      price: 0,
      period: 'Free Forever',
      tier: 'FREE TIER',
      description: 'Get started with basic immigration assistance',
      features: [
        '10 total questions per month',
        '1 AI agent access (Ask Questions only)',
        '1 USCIS notice scan per month',
        'Start Free Case Review access',
        '❌ No AI Immigration Assistant',
        '❌ No "Scan My USCIS Notice"',
        '❌ No "Review My Case" modal'
      ],
      highlight: false,
      badge: 'FREE',
      restrictions: [
        'Only "Ask a Question" modal',
        'No advanced case review features',
        'Limited feature access'
      ]
    },
    {
      id: 'Navigator',
      name: 'Navigator', 
      price: 39,
      period: 'per month',
      tier: 'BASIC TIER',
      description: 'Enhanced features for active cases',
      features: [
        '200 AI interactions per month',
        '1 AI agent (full access)',
        '15 document uploads per month',
        'Ask Questions + Review My Case modals',
        '✅ AI Immigration Assistant',
        '✅ Scan My USCIS Notice',
        '✅ Start Free Case Review',
        'AI-generated cover letters',
        'Evidence summaries',
        'Case analysis with strength scores',
        'RFE/NOID response generation'
      ],
      highlight: false,
      badge: 'BASIC'
    },
    {
      id: 'Advocate',
      name: 'Advocate',
      price: 79,
      period: 'per month',
      tier: 'PROFESSIONAL TIER',
      description: 'Comprehensive tools for complex cases',
      features: [
        '500 AI interactions per month',
        '4 AI agents (full access)',
        '20 document uploads per month',
        'Ask Questions + Review My Case modals',
        '✅ All Navigator features',
        'Advanced form-filling assistant',
        'Export completed forms (multiple formats)',
        'In-depth case analysis',
        'Red flag detection',
        'Improvement plans'
      ],
      highlight: true,
      badge: 'MOST POPULAR'
    },
    {
      id: 'Champion',
      name: 'Champion',
      price: 149,
      period: 'per month',
      tier: 'PREMIUM TIER',
      description: 'Unlimited access for professionals',
      features: [
        'Unlimited AI interactions',
        'All 8 AI agents (unlimited access)',
        'Unlimited document uploads',
        'Unlimited document scans',
        '✅ All Advocate features',
        'Integration with Attorney Network',
        'Priority email support (24-hour)',
        'Unlimited NOID/RFE response drafting',
        'Export capabilities',
        'Premium support'
      ],
      highlight: false,
      badge: 'PREMIUM'
    }
  ];

  const handlePlanSelection = async (planId) => {
    setSelectedPlan(planId);
    setIsProcessing(true);
    setError('');

    try {
      if (!user) {
        setError('Please log in to select a plan');
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const originUrl = window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          package_id: planId,
          package_type: 'subscription',
          origin_url: originUrl
        }),
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create checkout session');
      }
      
      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe checkout for paid plans
        window.location.href = data.url;
      } else if (data.message) {
        // Handle free tier or successful subscription activation
        // Navigator and Advocate plans need agent selection
        // Champion gets all 8 agents automatically, Explorer gets 1 agent automatically
        if (planId === 'Navigator' || planId === 'Advocate') {
          setShowAgentSelection(true);
        } else {
          // Explorer and Champion plans - go to dashboard (no agent selection needed)
          navigate('/?onboarding_complete=true');
        }
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Plan selection error:', error);
      setError(error.message || 'An error occurred while selecting the plan');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAgentSelectionComplete = (data) => {
    console.log('Agent selection completed:', data);
    // Redirect to dashboard after agent selection is complete
    navigate('/?onboarding_complete=true');
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'FREE':
        return 'bg-green-500';
      case 'POPULAR':
        return 'bg-blue-500';
      case 'MOST POPULAR':
        return 'bg-purple-500';
      case 'PREMIUM':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!user) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Choose Your Plan</h1>
          <p className="text-gray-400 text-lg">Select the plan that best fits your immigration needs</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/30 border border-red-400 text-red-400 p-4 rounded-lg mb-6 text-center max-w-2xl mx-auto">
            {error}
          </div>
        )}

        {/* Pricing Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-gray-800 rounded-xl p-6 transition-all duration-300 hover:transform hover:scale-105 ${
                plan.highlight ? 'ring-2 ring-purple-500 ring-opacity-50' : ''
              } ${
                selectedPlan === plan.id ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              {/* Badge */}
              <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium text-white ${getBadgeColor(plan.badge)}`}>
                {plan.badge}
              </div>

              {/* Plan Header */}
              <div className="text-center mb-6 pt-2">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="mb-3">
                  <span className="text-3xl font-bold text-white">
                    ${plan.price}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-gray-400 ml-1">/{plan.period}</span>
                  )}
                  {plan.price === 0 && (
                    <span className="text-gray-400 ml-1">{plan.period}</span>
                  )}
                </div>
                <p className="text-gray-400 text-sm">{plan.description}</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <div className="text-green-400 mr-3 mt-1">✓</div>
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* Select Button */}
              <button
                onClick={() => handlePlanSelection(plan.id)}
                disabled={isProcessing}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                  plan.highlight
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                } ${
                  isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isProcessing && selectedPlan === plan.id ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Processing...
                  </div>
                ) : (
                  `Select ${plan.name}`
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="text-center text-gray-400 text-sm mb-8">
          <p>✓ Cancel anytime • ✓ 30-day money-back guarantee • ✓ Secure payment processing</p>
        </div>

        {/* Pay-per-use Add-ons Section */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Pay-Per-Use Add-Ons</h2>
            <p className="text-gray-400">Available for all users regardless of plan</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* RFE/NOID Response Draft */}
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-white mb-2">RFE/NOID Response Draft</h3>
                <div className="text-2xl font-bold text-blue-400 mb-2">$29.99</div>
                <p className="text-gray-400 text-sm">One-time purchase</p>
              </div>
              <ul className="space-y-2 text-sm text-gray-300 mb-6">
                <li>✓ AI-generated draft letter tailored to USCIS concerns</li>
                <li>✓ Evidence checklist + suggested affidavits</li>
                <li>✓ Red flag detection</li>
                <li>✓ Risk flags + improvement recommendations</li>
                <li>✓ Case strength assessment</li>
                <li>✓ Full USCIS-ready response packet</li>
              </ul>
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg text-sm transition-colors">
                Purchase Add-On
              </button>
            </div>

            {/* AI Case Review */}
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-white mb-2">AI Case Review</h3>
                <div className="text-2xl font-bold text-blue-400 mb-2">$29.99</div>
                <p className="text-gray-400 text-sm">One-time purchase</p>
              </div>
              <ul className="space-y-2 text-sm text-gray-300 mb-6">
                <li>✓ Upload case details/documents → Get strength score</li>
                <li>✓ Weakness report and improvement suggestions</li>
                <li>✓ AI-generated draft response letter</li>
                <li>✓ Full USCIS-ready packet</li>
              </ul>
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg text-sm transition-colors">
                Purchase Add-On
              </button>
            </div>

            {/* Form-Filling Assistance */}
            <div className="bg-gray-800 rounded-xl p-6">
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-white mb-2">Form-Filling Assistance</h3>
                <div className="text-2xl font-bold text-blue-400 mb-2">$19.99</div>
                <p className="text-gray-400 text-sm">Per form</p>
              </div>
              <ul className="space-y-2 text-sm text-gray-300 mb-6">
                <li>✓ AI-guided form completion for I-130, I-485, N-400, etc.</li>
                <li>✓ Real-time error checking</li>
                <li>✓ Export as PDF or printable format</li>
              </ul>
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg text-sm transition-colors">
                Purchase Add-On
              </button>
            </div>
          </div>
        </div>

        {/* Skip for now button */}
        <div className="text-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            Skip for now and use free plan
          </button>
        </div>
      </div>

      {/* Agent Selection Modal */}
      <AgentSelectionModal
        isOpen={showAgentSelection}
        onClose={() => setShowAgentSelection(false)}
        onComplete={handleAgentSelectionComplete}
      />
    </div>
  );
};

export default PricingPage;